import React, { useEffect, useState, useRef, useContext } from "react";
import { 
  Table, 
  Input, 
  Button, 
  Space, 
  Spin, 
  Alert, 
  Card, 
  Typography, 
  Row, 
  Col,
  Avatar,
  Tag,
  Tooltip,
  Modal,
  Form,
  Select,
  message,
  Badge
} from "antd";
import { 
  SearchOutlined, 
  CustomerServiceOutlined,
  MailOutlined,
  EyeOutlined,
  MessageOutlined,
  SendOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined
} from "@ant-design/icons";
import axios from "axios";
import { LanguageContext } from "./LanguageContext";
import AppAdminLayout from "./components/AdminLayout";

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const SupportList = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [responseModal, setResponseModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [form] = Form.useForm();
  const { language, setLanguage, translations } = useContext(LanguageContext);

  // Dummy data with status variations
  const dummyData = [
    {
      id: 1,
      title: "Login sorunu",
      description: "Sisteme giriş yapamıyorum, şifremi unuttum ve sıfırlama maili gelmiyor.",
      email: "user1@example.com",
      status: "open",
      priority: "high",
      createdAt: "2024-01-15",
      responses: []
    },
    {
      id: 2,
      title: "Ödeme hatası",
      description: "Kredi kartımdan para çekildi ama işlem tamamlanmadı.",
      email: "user2@example.com",
      status: "pending",
      priority: "urgent",
      createdAt: "2024-01-14",
      responses: [
        { message: "Konuyu inceliyoruz, en kısa sürede dönüş yapacağız.", date: "2024-01-14", admin: "Admin" }
      ]
    },
    {
      id: 3,
      title: "Profil güncelleme",
      description: "Profil bilgilerimi güncelleyemiyorum, kaydet butonuna bastığımda hata veriyor.",
      email: "user3@example.com",
      status: "resolved",
      priority: "medium",
      createdAt: "2024-01-13",
      responses: [
        { message: "Bu sorun çözülmüştür. Lütfen tekrar deneyin.", date: "2024-01-13", admin: "Admin" }
      ]
    },
    {
      id: 4,
      title: "Mobil uygulama çöküyor",
      description: "Android uygulaması sürekli çöküyor, kullanamıyorum.",
      email: "user4@example.com",
      status: "open",
      priority: "high",
      createdAt: "2024-01-12",
      responses: []
    },
    {
      id: 5,
      title: "Veri kaybı",
      description: "Verilerim silindi, geri getirebilir misiniz?",
      email: "user5@example.com",
      status: "urgent",
      priority: "urgent",
      createdAt: "2024-01-11",
      responses: []
    }
  ];

  useEffect(() => {
    // Simulate API call with dummy data
    const fetchData = async () => {
      try {
        // Simulating API delay
        await new Promise(resolve => setTimeout(resolve, 1000));
        setData(dummyData);
      } catch (err) {
        setError("Bir hata oluştu: " + err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const getStatusColor = (status) => {
    const colors = {
      open: "blue",
      pending: "orange", 
      resolved: "green",
      urgent: "red"
    };
    return colors[status] || "default";
  };

  const getStatusText = (status) => {
    const texts = {
      open: "Açık",
      pending: "Beklemede", 
      resolved: "Çözüldü",
      urgent: "Acil"
    };
    return texts[status] || status;
  };

  const getPriorityIcon = (priority) => {
    if (priority === "urgent") return <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />;
    if (priority === "high") return <ClockCircleOutlined style={{ color: '#fa8c16' }} />;
    return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
  };

  const handleResponse = (record) => {
    setSelectedTicket(record);
    setResponseModal(true);
  };

  const handleSendResponse = async (values) => {
    try {
      // Simulate API call
      const newResponse = {
        message: values.response,
        date: new Date().toISOString().split('T')[0],
        admin: "Admin"
      };
      
      // Update data
      setData(prev => prev.map(item => 
        item.id === selectedTicket.id 
          ? { 
              ...item, 
              responses: [...item.responses, newResponse],
              status: values.status || item.status
            }
          : item
      ));
      
      message.success('Cevap gönderildi!');
      setResponseModal(false);
      form.resetFields();
    } catch (error) {
      message.error('Bir hata oluştu!');
    }
  };

  const columns = [
    {
      title: '#',
      dataIndex: 'id',
      key: 'id',
      width: 50,
      render: (id) => <Text strong>#{id}</Text>
    },
    {
      title: 'Talep',
      key: 'request',
      width: 250,
      render: (_, record) => (
        <div>
          <Space size={4}>
            {getPriorityIcon(record.priority)}
            <Text strong style={{ fontSize: '13px' }}>{record.title}</Text>
          </Space>
          <br />
          <Text type="secondary" style={{ fontSize: '11px' }}>
            {record.email}
          </Text>
          <br />
          <Text type="secondary" style={{ fontSize: '10px' }}>
            {record.createdAt}
          </Text>
        </div>
      ),
    },
    {
      title: 'Açıklama',
      dataIndex: 'description',
      key: 'description',
      width: 300,
      render: (description) => (
        <Tooltip title={description}>
          <Text style={{ 
            fontSize: '12px',
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {description}
          </Text>
        </Tooltip>
      )
    },
    {
      title: 'Durum',
      key: 'status',
      width: 100,
      render: (_, record) => (
        <Tag color={getStatusColor(record.status)} size="small">
          {getStatusText(record.status)}
        </Tag>
      ),
    },
    {
      title: 'Cevaplar',
      key: 'responses',
      width: 80,
      render: (_, record) => (
        <Badge count={record.responses.length} size="small">
          <MessageOutlined style={{ color: record.responses.length > 0 ? '#1890ff' : '#d9d9d9' }} />
        </Badge>
      ),
    },
    {
      title: 'İşlemler',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Görüntüle">
            <Button 
              size="small" 
              icon={<EyeOutlined />}
              onClick={() => handleResponse(record)}
            />
          </Tooltip>
          <Tooltip title="Cevapla">
            <Button 
              type="primary" 
              size="small" 
              icon={<SendOutlined />}
              onClick={() => handleResponse(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  if (loading) {
    return (
      <AppAdminLayout>
        <div style={{ padding: "16px", display: "flex", justifyContent: "center", alignItems: "center", height: "200px" }}>
          <Spin size="large" />
        </div>
      </AppAdminLayout>
    );
  }

  if (error) {
    return (
      <AppAdminLayout>
        <div style={{ padding: "16px" }}>
          <Alert message="Hata" description={error} type="error" showIcon />
        </div>
      </AppAdminLayout>
    );
  }

  const statusCounts = data.reduce((acc, item) => {
    acc[item.status] = (acc[item.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <AppAdminLayout>
      <div style={{ padding: "16px" }}>
        {/* Compact Header */}
        <Row justify="space-between" align="middle" style={{ marginBottom: "16px" }}>
          <Col>
            <Space align="center">
              <Avatar icon={<CustomerServiceOutlined />} size="default" />
              <div>
                <Title level={4} style={{ margin: 0 }}>Destek Talepleri</Title>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  {data.length} talep
                </Text>
              </div>
            </Space>
          </Col>
          <Col>
            <Space size="small">
              <Tag color="blue" size="small">Toplam: {data.length}</Tag>
              <Tag color="orange" size="small">Açık: {statusCounts.open || 0}</Tag>
              <Tag color="red" size="small">Acil: {statusCounts.urgent || 0}</Tag>
            </Space>
          </Col>
        </Row>

        {/* Compact Table */}
        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          size="small"
          pagination={{
            size: "small",
            defaultPageSize: 15,
            showSizeChanger: true,
            pageSizeOptions: ['10', '15', '25', '50'],
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} / ${total}`,
          }}
          scroll={{ x: 900 }}
          bordered
        />

        {/* Response Modal */}
        <Modal
          title={
            <Space>
              <CustomerServiceOutlined />
              <Text>Destek Talebi #{selectedTicket?.id}</Text>
            </Space>
          }
          open={responseModal}
          onCancel={() => {
            setResponseModal(false);
            form.resetFields();
          }}
          footer={null}
          width={700}
        >
          {selectedTicket && (
            <div>
              {/* Ticket Details */}
              <Card size="small" style={{ marginBottom: 16, backgroundColor: '#f5f5f5' }}>
                <Row gutter={16}>
                  <Col span={12}>
                    <Text strong>Başlık: </Text>{selectedTicket.title}
                  </Col>
                  <Col span={12}>
                    <Text strong>E-posta: </Text>{selectedTicket.email}
                  </Col>
                  <Col span={24} style={{ marginTop: 8 }}>
                    <Text strong>Açıklama: </Text>{selectedTicket.description}
                  </Col>
                </Row>
              </Card>

              {/* Previous Responses */}
              {selectedTicket.responses.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <Text strong>Önceki Cevaplar:</Text>
                  {selectedTicket.responses.map((response, index) => (
                    <Card key={index} size="small" style={{ marginTop: 8 }}>
                      <Text style={{ fontSize: '12px' }}>{response.message}</Text>
                      <div style={{ marginTop: 4 }}>
                        <Text type="secondary" style={{ fontSize: '10px' }}>
                          {response.admin} - {response.date}
                        </Text>
                      </div>
                    </Card>
                  ))}
                </div>
              )}

              {/* Response Form */}
              <Form
                form={form}
                onFinish={handleSendResponse}
                layout="vertical"
              >
                <Form.Item
                  label="Durum Güncelle"
                  name="status"
                  initialValue={selectedTicket.status}
                >
                  <Select>
                    <Option value="open">Açık</Option>
                    <Option value="pending">Beklemede</Option>
                    <Option value="resolved">Çözüldü</Option>
                    <Option value="urgent">Acil</Option>
                  </Select>
                </Form.Item>

                <Form.Item
                  label="Cevabınız"
                  name="response"
                  rules={[{ required: true, message: 'Lütfen bir cevap yazın!' }]}
                >
                  <TextArea 
                    rows={4} 
                    placeholder="Müşteriye cevabınızı yazın..."
                  />
                </Form.Item>

                <Form.Item style={{ marginBottom: 0 }}>
                  <Space>
                    <Button type="primary" htmlType="submit" icon={<SendOutlined />}>
                      Cevap Gönder
                    </Button>
                    <Button onClick={() => {
                      setResponseModal(false);
                      form.resetFields();
                    }}>
                      İptal
                    </Button>
                  </Space>
                </Form.Item>
              </Form>
            </div>
          )}
        </Modal>
      </div>

      <style jsx>{`
        .ant-table-thead > tr > th {
          background-color: #fafafa;
          font-weight: 600;
          padding: 8px;
          font-size: 12px;
        }
        .ant-table-tbody > tr > td {
          padding: 8px;
        }
        .ant-table-tbody > tr:hover > td {
          background-color: #e6f7ff !important;
        }
        .ant-btn-sm {
          height: 24px;
          padding: 0 7px;
          font-size: 12px;
        }
        .ant-tag {
          margin: 0;
          padding: 0 6px;
          font-size: 11px;
          line-height: 20px;
        }
      `}</style>
    </AppAdminLayout>
  );
};

export default SupportList;